import React, { useState } from 'react'
import { Modal, Button, Form, Alert, Spinner, Badge } from 'react-bootstrap'
import {
  PnsMacMiniUser,
  PnsMacMiniUserStatus,
} from 'interfaces/pns-mac-mini-user'
import useUpdateMacMiniUserStatus from 'hooks/use-update-mac-mini-user-status'
import { MacMiniUserActivationModal } from 'components/mac-mini-user-activation'

interface MacMiniUserStatusUpdateModalProps {
  show: boolean
  onHide: () => void
  macMiniUser: PnsMacMiniUser
  onSuccess?: () => void
}

const MacMiniUserStatusUpdateModal: React.FC<
  MacMiniUserStatusUpdateModalProps
> = ({ show, onHide, macMiniUser, onSuccess }) => {
  const [selectedStatus, setSelectedStatus] = useState<PnsMacMiniUserStatus>(
    macMiniUser.status
  )
  const [showConfirmation, setShowConfirmation] = useState(false)
  const [showActivationModal, setShowActivationModal] = useState(false)

  const { mutate: updateMacMiniUserStatus, isLoading } =
    useUpdateMacMiniUserStatus()

  const getStatusVariant = (status: PnsMacMiniUserStatus) => {
    switch (status) {
      case PnsMacMiniUserStatus.AVAILABLE:
        return 'info'
      case PnsMacMiniUserStatus.RESERVED:
        return 'warning'
      case PnsMacMiniUserStatus.ICLOUD_SETUP_IN_PROGRESS:
        return 'info'
      case PnsMacMiniUserStatus.ACTIVE:
        return 'success'
      case PnsMacMiniUserStatus.INACTIVE:
        return 'secondary'
      case PnsMacMiniUserStatus.TERMINATED:
        return 'danger'
      default:
        return 'secondary'
    }
  }

  const getStatusLabel = (status: string) => {
    return status.charAt(0) + status.slice(1).toLowerCase().replace(/_/g, ' ')
  }

  const getStatusOptions = () => {
    return Object.values(PnsMacMiniUserStatus).map((status) => (
      <option key={status} value={status}>
        {getStatusLabel(status)}
      </option>
    ))
  }

  const handleReviewChange = () => {
    if (selectedStatus === macMiniUser.status) {
      onHide()
      return
    }

    // Check for special transition: ICLOUD_SETUP_IN_PROGRESS → ACTIVE
    if (
      macMiniUser.status === PnsMacMiniUserStatus.ICLOUD_SETUP_IN_PROGRESS &&
      selectedStatus === PnsMacMiniUserStatus.ACTIVE
    ) {
      // Show activation modal with WISP configuration
      setShowActivationModal(true)
      return
    }

    // Show standard confirmation
    setShowConfirmation(true)
  }

  const handleConfirmUpdate = () => {
    updateMacMiniUserStatus(
      {
        userId: macMiniUser.id,
        status: selectedStatus,
      },
      {
        onSuccess: () => {
          onSuccess?.()
          onHide()
        },
      }
    )
  }

  const handleCancel = () => {
    setShowConfirmation(false)
    setSelectedStatus(macMiniUser.status)
  }

  const resetModal = () => {
    setShowConfirmation(false)
    setShowActivationModal(false)
    setSelectedStatus(macMiniUser.status)
  }

  React.useEffect(() => {
    if (!show) {
      resetModal()
    }
  }, [show, macMiniUser.status])

  return (
    <>
      <Modal
        show={show && !showActivationModal}
        onHide={onHide}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {showConfirmation
              ? 'Confirm Status Change'
              : 'Update Mac Mini User Status'}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {!showConfirmation ? (
            // Status Selection View
            <>
              <div className="mb-3">
                <strong>User:</strong> {macMiniUser.username}
              </div>

              <div className="mb-3">
                <strong>Current Status:</strong>{' '}
                <Badge
                  variant={getStatusVariant(macMiniUser.status)}
                  className="ms-1"
                >
                  {getStatusLabel(macMiniUser.status)}
                </Badge>
              </div>

              <div className="mb-3">
                <strong>Select New Status:</strong>
                <Form.Control
                  as="select"
                  value={selectedStatus}
                  onChange={(e) =>
                    setSelectedStatus(e.target.value as PnsMacMiniUserStatus)
                  }
                  className="mt-2"
                >
                  {getStatusOptions()}
                </Form.Control>
              </div>

              <Alert variant="info">
                <strong>Note:</strong> Changing the user status may affect its
                availability and functionality within the system.
              </Alert>
            </>
          ) : (
            // Confirmation View
            <>
              <Alert variant="warning">
                <div className="fw-bold mb-2">⚠️ Confirm Status Change</div>
                <div>
                  You are about to change the status of {macMiniUser.username}.
                </div>
              </Alert>

              <div className="d-flex justify-content-between align-items-center p-3 bg-light rounded">
                <div>
                  <strong>Current Status:</strong>{' '}
                  {getStatusLabel(macMiniUser.status)}
                </div>
                <div>
                  <strong>New Status:</strong> {getStatusLabel(selectedStatus)}
                </div>
              </div>
            </>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={showConfirmation ? handleCancel : onHide}
            disabled={isLoading}
          >
            {showConfirmation ? 'Cancel' : 'Close'}
          </Button>
          <Button
            variant={showConfirmation ? 'warning' : 'primary'}
            onClick={
              showConfirmation ? handleConfirmUpdate : handleReviewChange
            }
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Updating...
              </>
            ) : showConfirmation ? (
              'Confirm Change'
            ) : (
              'Review Change'
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Mac Mini User Activation Modal for ICLOUD_SETUP_IN_PROGRESS → ACTIVE */}
      <MacMiniUserActivationModal
        show={showActivationModal}
        onHide={() => setShowActivationModal(false)}
        macMiniUser={macMiniUser}
        onSuccess={() => {
          setShowActivationModal(false)
          onSuccess?.()
          onHide()
        }}
        onCancel={() => setShowActivationModal(false)}
      />
    </>
  )
}

export default MacMiniUserStatusUpdateModal
